import { useEffect, useState } from "react";
import { V2PagingMeta } from "@generated";
import { OptionType } from "app/javascript/src/pages/TravelAndExpenseV2/types/Expense";

interface PagedResponse<TItem> {
  data?: TItem[];
  meta?: V2PagingMeta;
}

type PagedQueryHook = (args: any) => {
  data?: PagedResponse<any>;
  isFetching: boolean;
};

// The record type the list hook returns, e.g. V2VendorResource.
type ItemOf<THook extends PagedQueryHook> = NonNullable<
  NonNullable<ReturnType<THook>["data"]>["data"]
>[number];

interface UseInfiniteSelectOptionsArgs<THook extends PagedQueryHook> {
  // A generated v2 list hook, e.g. useV2ListVendorsForAccountQuery.
  useQuery: THook;
  toOption: (item: ItemOf<THook>) => OptionType;
  // Filters sent with every request, e.g. { active: true }.
  filter?: Record<string, unknown>;
  // The filter the typed text is sent as.
  searchFilter?: string;
  // Omit to use the API's default page size.
  pageSize?: number;
  minSearchLength?: number;
}

/**
 * Options and handlers for a select that searches the API as the user types
 * and loads the next page when the menu is scrolled to the bottom. The result
 * can be spread onto Select2V2.
 */
const useInfiniteSelectOptions = <THook extends PagedQueryHook>({
  useQuery,
  toOption,
  filter = {},
  searchFilter = "name",
  pageSize,
  minSearchLength = 2,
}: UseInfiniteSelectOptionsArgs<THook>) => {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [options, setOptions] = useState<OptionType[]>([]);

  // Only send what is needed, so an unfiltered first page uses the same args
  // (and RTK cache entry) as a plain `useQuery({})` elsewhere on the page.
  const filters = search ? { ...filter, [searchFilter]: search } : filter;
  const paging = {
    ...(page > 1 && { number: page }),
    ...(pageSize && { size: pageSize }),
  };
  const { data, isFetching } = useQuery({
    ...(Object.keys(filters).length && { filter: filters }),
    ...(Object.keys(paging).length && { page: paging }),
  });

  // Page 1 replaces the list (a new search); later pages extend it.
  useEffect(() => {
    if (!data?.data) return;
    const pageOptions = data.data.map(toOption);
    setOptions((prev) => {
      const base = page === 1 ? [] : prev;
      const seen = new Set(base.map((o) => o.value));
      return [...base, ...pageOptions.filter((o) => !seen.has(o.value))];
    });
  }, [data]);

  const onInputChange = (inputValue: string) => {
    if (inputValue.length >= minSearchLength || inputValue.length === 0) {
      setSearch(inputValue);
      setPage(1);
    }
  };

  const onMenuScrollToBottom = () => {
    const pageCount = data?.meta?.["page-count"] ?? 1;
    if (!isFetching && page < pageCount) {
      setPage(page + 1);
    }
  };

  return {
    options,
    isLoading: isFetching,
    onInputChange,
    onMenuScrollToBottom,
  };
};

export default useInfiniteSelectOptions;
